import React, { useEffect, useState } from 'react';
import { Settings as SettingsIcon, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ADMIN_NAV } from '../../constants/nav';
import { adminGetSettings, adminUpdateSettings } from '../../features/settings/api/settings';
import type { SiteSettings } from '../../types';
import { SETTINGS } from '../../constants/testIds';

const inputClass = 'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';
const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';

type Tab = 'general' | 'seo' | 'tracking';

export default function AdminSettingsPage() {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<Tab>('general');

  useEffect(() => { adminGetSettings().then(setSettings).finally(() => setLoading(false)); }, []);

  const set = (k: keyof SiteSettings, v: string) => setSettings((s) => (s ? { ...s, [k]: v } : s));
  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try { const s = await adminUpdateSettings(settings); setSettings(s); toast.success('Pengaturan tersimpan'); }
    catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const field = (k: keyof SiteSettings, label: string, testId?: string, placeholder = '', textarea = false) => (
    <div>
      <label className={labelClass}>{label}</label>
      {textarea
        ? <textarea data-testid={testId} className={inputClass} rows={2} value={settings?.[k] || ''} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} />
        : <input data-testid={testId} className={inputClass} value={settings?.[k] || ''} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} />}
    </div>
  );

  const tabBtn = (t: Tab, label: string, testId: string) => (
    <button data-testid={testId} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-sm transition-colors ${tab === t ? 'bg-cyan-400 text-[#050914] font-medium' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>{label}</button>
  );

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Settings">
      <div className="max-w-3xl" data-testid={SETTINGS.adminPage}>
        <div className="mb-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center"><SettingsIcon className="w-5 h-5 text-cyan-400" strokeWidth={1.5} /></div>
          <div><p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Konfigurasi</p><h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Settings</h2></div>
        </div>

        {loading || !settings ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div> : (
          <>
            <div className="flex gap-2 mb-6 bg-white/[0.02] border border-white/10 rounded-xl p-1.5 w-fit">
              {tabBtn('general', 'General', SETTINGS.tabGeneral)}
              {tabBtn('seo', 'SEO / Meta', SETTINGS.tabSeo)}
              {tabBtn('tracking', 'Tracking', SETTINGS.tabTracking)}
            </div>

            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 space-y-3">
              {tab === 'general' && (
                <>
                  {field('site_name', 'Nama Situs', SETTINGS.siteName)}
                  {field('logo_url', 'Logo URL', SETTINGS.logoUrl, 'https://...')}
                  {field('favicon_url', 'Favicon URL', SETTINGS.faviconUrl, 'https://...')}
                  {field('support_whatsapp', 'WhatsApp Support', SETTINGS.supportWa, '6281234567890')}
                  {field('admin_notification_email', 'Email Notifikasi Admin (menerima alert user baru, pembayaran, VIP, dll.)', 'settings-admin-notification-email', 'admin@cuantrader.id')}
                  {field('social_whatsapp_channel_url', 'Link Channel WhatsApp', 'settings-wa-channel', 'https://whatsapp.com/channel/...')}
                  {field('social_telegram_url', 'Link Channel Telegram', 'settings-telegram-url', 'https://t.me/cuantrader')}
                  {field('social_instagram_url', 'Link Instagram', 'settings-instagram-url', 'https://instagram.com/cuantrader')}
                  {field('social_facebook_url', 'Link Facebook (Meta)', 'settings-facebook-url', 'https://facebook.com/cuantrader')}
                  {field('social_tiktok_url', 'Link TikTok', 'settings-tiktok-url', 'https://tiktok.com/@cuantrader')}
                  {field('social_discord_url', 'Link Discord', 'settings-discord-url', 'https://discord.gg/xxxx')}
                  {field('welcome_video_url', 'URL Video Welcome (YouTube)', 'settings-welcome-video', 'https://youtube.com/watch?v=...')}
                  {field('welcome_note', 'Teks Sambutan / Arahan untuk User Baru', 'settings-welcome-note', 'Selamat datang! Berikut arahan singkat...', true)}
                </>
              )}
              {tab === 'seo' && (
                <>
                  {field('default_meta_title', 'Meta Title Default')}
                  {field('default_meta_description', 'Meta Description Default', undefined, '', true)}
                  {field('default_meta_keywords', 'Meta Keywords', undefined, 'trading, forex, ...')}
                  {field('default_og_image_url', 'OG Image URL', undefined, 'https://...')}
                  {field('canonical_base_url', 'Canonical Base URL', undefined, 'https://cuantrader.com')}
                </>
              )}
              {tab === 'tracking' && (
                <>
                  {field('meta_pixel_id', 'Meta / Facebook Pixel ID', SETTINGS.metaPixel, '1234567890')}
                  {field('tiktok_pixel_id', 'TikTok Pixel ID', SETTINGS.tiktokPixel, 'ABCDE12345')}
                  {field('gtm_id', 'Google Tag Manager ID', undefined, 'GTM-XXXXXX')}
                </>
              )}
            </div>

            <button data-testid={SETTINGS.saveBtn} onClick={save} disabled={saving} className="mt-5 bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Save className="w-4 h-4" strokeWidth={2} />} Simpan Pengaturan
            </button>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
